import { useAtomValue } from 'jotai';
import { useMatch } from 'react-router-dom';
import { unwrappedMetricLookupMapAtom } from '@/atoms/queries/metric';
import { playerRadarTableColumnsAtom, teamRadarTableColumnsAtom } from '@/atoms/queries/tableColumns';
import { radarTemplateCustomLimitsAtom, radarTemplateLimitModesAtom } from '@/atoms/radar';
import {
  currentFormattedMinMaxSliderValues,
  getSafeNinetyFifthPercentile,
  roundToFixed,
} from '@/components/modal/CreateRadarTemplateModal/metricLimitsUtils';
import { toDisplayValue } from '@/components/sliders/sliderUtils';
import { useMetricDistributionAtoms } from '@/hooks/useMetricDistributionAtoms';
import { getMetricDistribution, getPhysicalMetricPrecision } from '@/utils/metrics';

/** Swap reverse scale metrics to maintain higher-to-lower display */
const mixMaxInCorrectOrder = (reverseScale: boolean, min: string, max: string) =>
  reverseScale ? { min: max, max: min } : { min, max };

export const useCurrentFormattedMinMaxMetricValues = () => {
  const limitModes = useAtomValue(radarTemplateLimitModesAtom);
  const customLimits = useAtomValue(radarTemplateCustomLimitsAtom);
  const { percentiles } = useMetricDistributionAtoms();

  const isPlayerSectionByRoute = !!useMatch('/:locale/player/*');
  const radarColumnsAtom = isPlayerSectionByRoute ? playerRadarTableColumnsAtom : teamRadarTableColumnsAtom;
  const radarColumns = useAtomValue(radarColumnsAtom);
  const metricLookupMap = useAtomValue(unwrappedMetricLookupMapAtom);

  const getCurrentFormattedMinMaxMetricValues = (metric: string): { min: string; max: string } => {
    const metricDistribution = getMetricDistribution(metric, percentiles);

    const isCustomMode = limitModes[metric] === 'custom';
    const physicalOverride = getPhysicalMetricPrecision(metric, metricLookupMap, radarColumns);
    const precision = physicalOverride?.precision ?? metricDistribution.precision;
    const { percentage: isPercentage } = metricDistribution;

    if (isCustomMode && customLimits[metric]) {
      const { currentMin, currentMax } = currentFormattedMinMaxSliderValues(
        metric,
        customLimits,
        metricDistribution,
        isPercentage,
      );
      const roundedMin = roundToFixed(currentMin, precision);
      const roundedMax = roundToFixed(currentMax, precision);
      return mixMaxInCorrectOrder(metricDistribution.reverseScale, roundedMin, roundedMax);
    }

    if (physicalOverride) {
      const p5 = roundToFixed(
        toDisplayValue(metricDistribution.percentiles[5].value, isPercentage),
        physicalOverride.precision,
      );
      const p95 = roundToFixed(
        toDisplayValue(metricDistribution.percentiles[95].value, isPercentage),
        physicalOverride.precision,
      );
      const safeP95 = getSafeNinetyFifthPercentile(p5, p95);
      return mixMaxInCorrectOrder(metricDistribution.reverseScale, p5, `${safeP95}`);
    }

    const fifthPercentile = metricDistribution.percentiles[5].formattedValue;
    const ninetyFifthPercentile = metricDistribution.percentiles[95].formattedValue;
    const safeNinetyFifthPercentile = getSafeNinetyFifthPercentile(fifthPercentile, ninetyFifthPercentile);

    return mixMaxInCorrectOrder(metricDistribution.reverseScale, fifthPercentile, `${safeNinetyFifthPercentile}`);
  };

  return getCurrentFormattedMinMaxMetricValues;
};
